import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAllpnrsCount, getAllUsersCount } from "./dashBoardController";
import { Users, Briefcase } from "lucide-react";

const Card: React.FC<
  React.PropsWithChildren<{ className?: string; onClick?: () => void }>
> = ({ children, className = "", onClick }) => (
  <div
    className={`bg-white shadow-md rounded-lg overflow-hidden ${className}`}
    onClick={onClick}
  >
    {children}
  </div>
);

const CardHeader: React.FC<React.PropsWithChildren<{ className?: string }>> = ({
  children,
  className = "",
}) => (
  <div className={`px-6 py-4 bg-gray-50 border-b border-gray-200 ${className}`}>
    {children}
  </div>
);

const CardContent: React.FC<
  React.PropsWithChildren<{ className?: string }>
> = ({ children, className = "" }) => (
  <div className={`px-6 py-4 ${className}`}>{children}</div>
);

interface StatCardWidgetProps {
  name: string;
  count: number;
  icon: React.ReactNode;
  color: string;
  onClick?: () => void;
}

const StatCardWidget: React.FC<StatCardWidgetProps> = ({
  name,
  count,
  icon,
  color,
  onClick,
}) => {
  return (
    <Card
      className={`bg-gradient-to-br ${color} text-white cursor-pointer`}
      onClick={onClick}
    >
      <CardContent className="flex items-center justify-between">
        <div>
          <h3 className="text-3xl font-bold mb-1">{count.toLocaleString()}</h3>
          <p className="text-sm opacity-80">{name}</p>
        </div>
        <div className="text-4xl opacity-80">{icon}</div>
      </CardContent>
    </Card>
  );
};

const Dashboard: React.FC = () => {
  const [userCount, setUserCount] = useState(0);
  const [pnrCount, setPnrCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStatData = async () => {
      const usersCount = await getAllUsersCount();
      const pnrtCount = await getAllpnrsCount();
      setUserCount(usersCount);
      setPnrCount(pnrtCount);
    };
    fetchStatData();
  }, []);

  return (
    <div className="container mx-auto p-4">
      <h1 className="text-3xl font-bold mb-6">Dashboard</h1>

      <Card className="mb-8">
        <CardHeader>
          <h2 className="text-xl font-semibold text-gray-800">
            Welcome to the Air Connect Admin Dashboard
          </h2>
        </CardHeader>
        <CardContent>
          <p className="text-gray-600">
            Here you can monitor key statistics and manage various aspects of
            your application.
          </p>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCardWidget
          name="Active Users"
          count={userCount}
          icon={<Users />}
          color="from-blue-500 to-blue-600"
          onClick={() => navigate("/users")}
        />
        <StatCardWidget
          name="Bookings"
          count={pnrCount}
          icon={<Briefcase />}
          color="from-green-500 to-green-600"
          onClick={() => navigate("/bookings")}
        />
      </div>

      <Card>
        <CardHeader>
          <h2 className="text-xl font-semibold text-gray-800">
            Recent Activities
          </h2>
        </CardHeader>
        <CardContent>
          <p className="text-gray-600">
            This section can display recent bookings, user registrations, or
            other important events.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default Dashboard;
